import { DateFormatter } from 'infrastructure/functions';
import { DateFormats, ModuleType } from 'infrastructure/enums';
import { Fragment, type FC } from 'react';
import { PlayIcon } from 'components/atoms/icons/play';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import ReadingCallAudioPlayer from 'components/molecules/reading-call-audio-player';
import LinkedReadingCallsIcon from 'components/atoms/icons/linked-readings-call';

import s from './styles.module.scss';

import type { IPatientThresholds } from 'infrastructure/interfaces';
import type { ReadingStatus, ReadingType } from 'infrastructure/enums';
import type {
  ILinkedReadingCall,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

interface LinkedCallsProps {
  linkedCalls: ILinkedReadingCall[];
  patientGuid: string;
  patientName: string;
  patientPhone: string;
  globalStatus: ReadingStatus;
  values: Value;
  statuses: TStatuses;
  thresholds: IPatientThresholds;
  readingType: ReadingType;
  thresholdName?: TThresholdName | null;
  patientTimezone: string;
}

const LinkedCalls: FC<LinkedCallsProps> = ({
  linkedCalls,
  patientGuid,
  patientName,
  patientPhone,
  globalStatus,
  values,
  statuses,
  thresholds,
  readingType,
  thresholdName,
  patientTimezone,
}) => {
  const { t } = useTranslation();

  return (
    <div className={s.wrapper} data-cy="linked-calls">
      <div className={s.title}>{t('linkedCallReadings.linkedCalls')}</div>
      <ul className={s['call-list']}>
        {linkedCalls.length === 0 && (
          <div className={s['empty-list-block']}>
            <LinkedReadingCallsIcon
              width={84}
              height={84}
              className={s['empty-icon']}
            />
            <p>{t('linkedCallReadings.noLinkedCalls')}</p>
          </div>
        )}
        {linkedCalls.map((call) => {
          return (
            <Fragment key={call.callId}>
              <li className={s['call-item']} data-cy="linked-calls-item">
                <div className={s.call}>
                  <ReadingCallAudioPlayer
                    callId={call.callId}
                    patientGuid={patientGuid}
                    module={ModuleType.RPM}
                    patientPhone={patientPhone}
                    patientName={patientName}
                    createdAt={call.createdAt}
                    globalStatus={globalStatus}
                    values={values}
                    statuses={statuses}
                    thresholds={thresholds}
                    readingType={readingType}
                    thresholdName={thresholdName}
                    linkedCallReadings={call.linkedCallReadings}
                    patientTimezone={patientTimezone}
                  >
                    {({ loading, disable, handleDownload }) => (
                      <BaseButton
                        icon={<PlayIcon />}
                        onClick={handleDownload}
                        type="secondary"
                        loading={loading}
                        disabled={disable}
                      />
                    )}
                  </ReadingCallAudioPlayer>
                  <p>{call.callId}</p>
                </div>
                <div className={s['call-info']}>
                  <p className={s['call-info-call-by']}>
                    {t('linkedCallReadings.callBy')}: {call.author}
                  </p>
                  <p className={s['call-info-date']}>
                    {DateFormatter({
                      date: call.createdAt,
                      format: DateFormats['MM-dd-yyyy | h:mm a'],
                    })}
                  </p>
                </div>
              </li>
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
};
export default LinkedCalls;
